import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Amplify } from 'aws-amplify';
import { generateClient } from 'aws-amplify/api';

export type SessionState =
  | { kind: 'checking' }
  | { kind: 'signed-out' }
  | { kind: 'ready' }
  | { kind: 'expired' }
  | { kind: 'unavailable' };

type Operation<T> = string & ({ __generatedQueryOutput: T } | { __generatedMutationOutput: T });
export interface Client {
  graphql<T extends Record<string, unknown>>(request: { query: Operation<T>; variables?: Record<string, unknown> }): Promise<{ data: T }>;
}

interface ClientContextType {
  client: Client | null;
  session: SessionState;
  checkTokenValidity: () => Promise<boolean>;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

function isAuthorizationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  if ('error' in error && typeof error.error === 'string' &&
      ['login_required', 'consent_required', 'interaction_required', 'invalid_grant', 'missing_refresh_token'].includes(error.error)) return true;
  if ('errorType' in error && typeof error.errorType === 'string' && /unauthorized|unauthenticated/i.test(error.errorType)) return true;
  if ('message' in error && typeof error.message === 'string' && /not authorized|unauthorized|unauthenticated/i.test(error.message)) return true;
  if ('errors' in error && Array.isArray(error.errors)) return error.errors.some(isAuthorizationError);
  return false;
}

Amplify.configure({ API: { GraphQL: {
  endpoint: 'https://navypoe54ndunnhkz3dj3ois3u.appsync-api.ap-northeast-1.amazonaws.com/graphql',
  region: 'ap-northeast-1',
  defaultAuthMode: 'oidc',
} } });
const api = generateClient();

export const ClientProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, user } = useAuth0();
  const [checkedSession, setCheckedSession] = useState<SessionState>({ kind: 'checking' });
  const identity = isLoading ? 'loading' : isAuthenticated ? user?.sub ?? 'authenticated' : 'signed-out';
  const rejectedToken = useRef(false);
  const activeIdentity = useRef(identity);
  activeIdentity.current = identity;
  const session: SessionState = isLoading ? { kind: 'checking' } : !isAuthenticated ? { kind: 'signed-out' } : checkedSession;

  const getToken = useCallback(async () => {
    if (!isAuthenticated || isLoading || activeIdentity.current !== identity) throw new Error('ログインが必要です。');
    try {
      const token = await getAccessTokenSilently({ timeoutInSeconds: 10, cacheMode: rejectedToken.current ? 'off' : 'on' });
      if (activeIdentity.current !== identity) throw new Error('ログイン状態が変わりました。');
      if (!token) {
        setCheckedSession({ kind: 'expired' });
        throw new Error('ログインが必要です。');
      }
      rejectedToken.current = false;
      setCheckedSession({ kind: 'ready' });
      return token;
    } catch (error) {
      if (activeIdentity.current === identity) {
        setCheckedSession({ kind: isAuthorizationError(error) || (error instanceof Error && error.message === 'ログインが必要です。') ? 'expired' : 'unavailable' });
      }
      throw error;
    }
  }, [getAccessTokenSilently, identity, isAuthenticated, isLoading]);

  const checkTokenValidity = useCallback(async () => {
    try {
      await getToken();
      return true;
    } catch {
      return false;
    }
  }, [getToken]);

  useEffect(() => {
    setCheckedSession({ kind: 'checking' });
    if (!isAuthenticated || isLoading) return;
    void checkTokenValidity();
    const checkWhenVisible = () => {
      if (document.visibilityState === 'visible') void checkTokenValidity();
    };
    window.addEventListener('focus', checkWhenVisible);
    document.addEventListener('visibilitychange', checkWhenVisible);
    const interval = window.setInterval(checkWhenVisible, 60_000);
    return () => {
      window.removeEventListener('focus', checkWhenVisible);
      document.removeEventListener('visibilitychange', checkWhenVisible);
      window.clearInterval(interval);
    };
  }, [isAuthenticated, isLoading, checkTokenValidity]);

  const authenticatedClient = useMemo<Client>(() => ({
    async graphql<T extends Record<string, unknown>>(request: { query: Operation<T>; variables?: Record<string, unknown> }) {
      const authToken = await getToken();
      try {
        const query: string = request.query;
        // Auth0 supplies Authorization; Amplify's oidc mode also requires its own token provider.
        const result = await api.graphql({ query, variables: request.variables, authMode: 'none', authToken });
        if (activeIdentity.current !== identity) throw new Error('ログイン状態が変わりました。');
        if (!('data' in result)) throw new Error('予約 API から応答がありません。');
        if (result.errors?.length) throw result;
        return { data: result.data };
      } catch (error) {
        if (activeIdentity.current === identity && isAuthorizationError(error)) {
          rejectedToken.current = true;
          setCheckedSession({ kind: 'expired' });
        }
        throw error;
      }
    },
  }), [getToken, identity]);

  return <ClientContext.Provider value={{ client: isAuthenticated && !isLoading ? authenticatedClient : null, session, checkTokenValidity }}>{children}</ClientContext.Provider>;
};

export const useClient = (): ClientContextType => {
  const context = useContext(ClientContext);
  if (!context) throw new Error('useClient must be used within a ClientProvider');
  return context;
};
